import { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Upload, FileText, Truck, AlertTriangle, ChevronRight,
  Loader2, CheckCircle2, X, Copy, AlertCircle, Trash2
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import Navigation from "@/components/Navigation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InvoiceScanPage() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [detail, setDetail] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => { loadInvoices(); }, []);

  const loadInvoices = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/invoices?limit=50`);
      setInvoices(res.data.invoices || []);
    } catch { setInvoices([]); }
    finally { setLoading(false); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const toastId = toast.loading(`Laddar upp ${file.name}...`, { duration: Infinity });
      const res = await axios.post(`${API}/invoices/scan`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 30000,
      });
      if (res.data.status === "processing") {
        toast.loading(`Skannar ${file.name} (0/${res.data.total_chunks || "?"} delar)...`, { id: toastId, duration: Infinity });
        pollStatus(res.data.id, toastId, file.name);
        loadInvoices();
      } else {
        toast.success(`${file.name}: ${res.data.shipment_count} forsandelser`, { id: toastId, duration: 5000 });
        loadInvoices();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Uppladdning misslyckades", { duration: 5000 });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const pollStatus = (id, toastId, filename) => {
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/invoices/${id}`);
        loadInvoices();
        if (res.data.status === "processed") {
          toast.success(`${filename} klar: ${res.data.shipment_count} forsandelser (${res.data.validation?.matched_references || 0} matchade)`, { id: toastId, duration: 8000 });
          return;
        }
        if (res.data.status === "error") {
          toast.error(`${filename} misslyckades: ${res.data.error || "okant fel"}`, { id: toastId, duration: 8000 });
          loadInvoices();
          return;
        }
        toast.loading(`Skannar ${filename} (${res.data.progress || "arbetar"})...`, { id: toastId, duration: Infinity });
        setTimeout(poll, 4000);
      } catch { setTimeout(poll, 6000); }
    };
    setTimeout(poll, 4000);
  };

  const openDetail = async (id) => {
    try {
      const res = await axios.get(`${API}/invoices/${id}`);
      setDetail(res.data);
      setShowDetail(true);
    } catch { /* ignore */ }
  };

  const deleteInvoice = async (id, e) => {
    if (e) e.stopPropagation();
    if (!confirm("Radera denna faktura och alla dess transportkostnader?")) return;
    try {
      await axios.delete(`${API}/invoices/${id}`);
      setInvoices(prev => prev.filter(inv => inv.id !== id));
      if (detail?.id === id) setShowDetail(false);
    } catch { /* ignore */ }
  };

  const formatDate = (d) => {
    if (!d) return "\u2014";
    return new Date(d).toLocaleDateString("sv-SE", { year: "numeric", month: "short", day: "numeric" });
  };

  const formatKr = (v) => {
    if (v === null || v === undefined) return "\u2014";
    return `${Math.round(v).toLocaleString("sv-SE")} kr`;
  };

  return (
    <div className="min-h-screen bg-background" data-testid="invoice-scan-page">
      <Navigation />
      <div className="container mx-auto px-4 py-6 max-w-5xl">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Transportfakturor</h1>
          <div>
            <input ref={fileInputRef} type="file" accept=".pdf" onChange={handleUpload} className="hidden" data-testid="invoice-file-input" />
            <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="upload-invoice-btn">
              {uploading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Laddar upp...</> : <><Upload className="h-4 w-4 mr-2" /> Skanna ny faktura</>}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground"><Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />Laddar fakturor...</div>
        ) : invoices.length === 0 ? (
          <Card className="p-12 text-center">
            <FileText className="h-16 w-16 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="font-heading text-xl font-bold mb-2">Inga fakturor skannats</h3>
            <p className="text-muted-foreground mb-4">Ladda upp en Bring- eller DSV-faktura (PDF) for att komma igang.</p>
            <Button onClick={() => fileInputRef.current?.click()} disabled={uploading}>
              <Upload className="h-4 w-4 mr-2" /> Skanna faktura
            </Button>
          </Card>
        ) : (
          <div className="space-y-2">
            {invoices.map((inv) => (
              <Card
                key={inv.id}
                className={`transition-colors ${inv.status === "processing" ? "opacity-70 animate-pulse" : "cursor-pointer hover:bg-muted/30"}`}
                onClick={() => inv.status !== "processing" && openDetail(inv.id)}
                data-testid={`invoice-card-${inv.id}`}
              >
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2.5 rounded-lg ${inv.status === "processing" ? "bg-blue-500/10" : inv.carrier === "bring" ? "bg-green-500/10" : "bg-blue-500/10"}`}>
                      {inv.status === "processing" ? (
                        <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />
                      ) : (
                        <Truck className={`h-5 w-5 ${inv.carrier === "bring" ? "text-green-500" : "text-blue-500"}`} />
                      )}
                    </div>
                    <div>
                      <p className="font-medium">
                        {inv.status === "processing" ? (
                          <>Skannar {inv.filename}... ({inv.progress || "startar"})</>
                        ) : inv.status === "error" ? (
                          <><AlertTriangle className="h-4 w-4 inline mr-1 text-red-500" />FEL: {inv.filename}</>
                        ) : (
                          <><span className="uppercase">{inv.carrier}</span> {"\u2014"} Faktura {inv.invoice_number}</>
                        )}
                      </p>
                      {inv.status !== "processing" && (
                        <p className="text-sm text-muted-foreground">
                          {formatDate(inv.invoice_date)} | {inv.shipment_count} forsandelser | {inv.filename}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {inv.ai_flags?.length > 0 && (
                      <Badge variant="destructive" className="text-xs">
                        <AlertTriangle className="h-3 w-3 mr-1" /> {inv.ai_flags.length}
                      </Badge>
                    )}
                    {inv.status !== "processing" && (
                      <span className="font-bold">{formatKr(inv.total_excl_vat)}</span>
                    )}
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-red-500" onClick={(e) => deleteInvoice(inv.id, e)} data-testid={`delete-invoice-${inv.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                    {inv.status !== "processing" && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Invoice Detail Dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                {detail?.carrier?.toUpperCase()} {"\u2014"} Faktura {detail?.invoice_number}
              </span>
              <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600 hover:bg-red-500/10" onClick={() => deleteInvoice(detail?.id)} data-testid="delete-invoice-detail">
                <Trash2 className="h-4 w-4 mr-1" /> Radera
              </Button>
            </DialogTitle>
            <DialogDescription>
              {formatDate(detail?.invoice_date)} | {detail?.shipment_count} forsandelser | Totalt: {formatKr(detail?.total_excl_vat)} exkl moms
              {detail?.validation && ` | ${detail.validation.matched_references} matchade, ${detail.validation.unmatched_references} omatchade`}
            </DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-4">
              {detail.ai_flags?.length > 0 && (
                <Card className="border-red-500/20 bg-red-500/5">
                  <CardContent className="p-3 space-y-1">
                    {detail.ai_flags.map((f, i) => (
                      <div key={i} className="flex items-center justify-between text-sm">
                        <span>
                          {f.flag === "unknown_reference" ? <AlertCircle className="h-3 w-3 inline mr-1 text-muted-foreground" /> :
                           f.flag === "duplicate_reference" ? <Copy className="h-3 w-3 inline mr-1 text-purple-500" /> :
                           <AlertTriangle className="h-3 w-3 inline mr-1 text-red-500" />}
                          Ref <span className="font-mono">{f.reference}</span> {f.recipient ? `(${f.recipient})` : ""}
                        </span>
                        <Badge variant={f.severity === "critical" ? "destructive" : f.flag === "unknown_reference" ? "outline" : "warning"} className="text-xs">
                          {f.flag === "unknown_reference" ? "Okand" : f.flag === "duplicate_reference" ? "Dubblett" : "Hog kostnad"}
                        </Badge>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
              <ScrollArea className="h-[400px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Referens</TableHead>
                      <TableHead>Mottagare</TableHead>
                      <TableHead className="text-right hidden sm:table-cell">Vikt</TableHead>
                      <TableHead className="text-right hidden md:table-cell">Frakt</TableHead>
                      <TableHead className="text-right hidden md:table-cell">Bransle</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="text-right">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {detail.shipments?.map((s, i) => {
                      const isHigh = detail.ai_flags?.some(f => f.reference === s.reference && f.flag === "high_cost");
                      const validated = s.reference_validated !== false;
                      return (
                        <TableRow key={i} className={isHigh ? "bg-red-500/5" : !validated ? "opacity-50" : ""}>
                          <TableCell className="font-mono font-medium">
                            {s.reference}
                            {isHigh && <AlertTriangle className="h-3 w-3 inline ml-1 text-red-500" />}
                            {s.reference_swapped && <span className="text-xs text-yellow-500 ml-1">(korr.)</span>}
                          </TableCell>
                          <TableCell className="text-sm">{s.recipient || "\u2014"}</TableCell>
                          <TableCell className="text-right hidden sm:table-cell">{s.weight_kg ? `${s.weight_kg} kg` : "\u2014"}</TableCell>
                          <TableCell className="text-right hidden md:table-cell">{formatKr(s.cost_breakdown?.freight)}</TableCell>
                          <TableCell className="text-right hidden md:table-cell">{formatKr(s.cost_breakdown?.fuel_surcharge)}</TableCell>
                          <TableCell className="text-right font-bold">{formatKr(s.total_cost)}</TableCell>
                          <TableCell className="text-right">
                            {validated ? (
                              <Badge variant="outline" className="text-green-500 border-green-500/30 text-xs">Matchad</Badge>
                            ) : (
                              <Badge variant="outline" className="text-muted-foreground text-xs">Okand</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </ScrollArea>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
